import { Injectable } from '@angular/core';
import { DataServerService } from '../api/data-server.service';
import { AlertService } from '../base/alert.service';
import { StorageService } from '../data/storage.service';
import { ImageCacheService } from '../data/image-cache.service';

@Injectable({
  providedIn: 'root',
})
export class ReportService {
  constructor(
    private dataserver: DataServerService,
    private alert: AlertService,
    private storage: StorageService,
    private imageCache: ImageCacheService
  ) {}

  /**
   * Ergänzt jedes Produkt um Proxy-URLs fuer das PDF und setzt den Payload-Flag
   * `showImages`. Das PDFMonkey-Template blendet die Bildspalte daran auf bzw. ab.
   * Die Original-URLs zeigen auf einen externen Host — PDFMonkey laedt sie ueber den
   * Kontor-Bildproxy, der die Bilder cached.
   *
   *   image      — Produktfoto (gilt fuer das ganze Modell, nicht pro Farbe)
   *   colorImage — Farbkaestchen der bestellten Variante (`colorcard`)
   *
   * Karma liefert kein farbspezifisches Produktfoto: `images` ist bei allen Farb-
   * varianten eines Modells identisch. Erst das Farbkaestchen macht die bestellte
   * Farbe eindeutig — deshalb werden beide angezeigt (wie in der Order-Uebersicht).
   *
   * `showImages` ist nur true, wenn der Schalter aktiv ist UND mindestens ein Produkt
   * ein Bild hat — sonst bliebe im PDF eine leere Spalte stehen.
   */
  private async withPdfImages(order: any): Promise<any> {
    const enabled = (await this.storage.getData<boolean>('pdfImagesEnabled')) !== false;
    const showSwatches = (await this.storage.getData<boolean>('pdfColorSwatches')) !== false;
    const grayscale = (await this.storage.getData<boolean>('pdfGrayscaleImages')) !== false;

    const products = (order.products ?? []).map((product: any) => {
      const photo = product.images?.[0];
      const swatch = product.colorcard;
      return {
        ...product,
        image: enabled && photo ? this.imageCache.proxyUrl(photo) : null,
        colorImage: enabled && showSwatches && swatch ? this.imageCache.proxyUrl(swatch) : null,
      };
    });

    const showImages = products.some((p: any) => !!p.image || !!p.colorImage);

    return Object.assign(order, { products, showImages, grayscaleImages: enabled && grayscale });
  }

  async createSendToCustomer(order: any, customerEmail: string = '', adminEmail: string = '') {
    const calc = this.getTotalTaxAmounts(order.products);
    calc.netPrice = Number(calc.netPrice.toFixed(2));
    calc.taxAmount = Number(calc.taxAmount.toFixed(2));
    calc.totalPrice = Number(calc.totalPrice.toFixed(2));
    calc.target = customerEmail.trim();
    calc.adminEmail = adminEmail.trim();

    order = Object.assign(order, { calc });
    order = await this.withPdfImages(order);

    this.alert.presentToast('Bitte warten... (ca. 15 Sekunden)', 9000);

    const res = await this.dataserver.generateConfirm(order);
    if (res.status === 200 || res.status === 201) {
      this.alert.presentToast('Bestätigung wurde erfolgreich erstellt und versendet');
    } else {
      this.alert.presentConfirmationAlert('Fehler', JSON.stringify(res.data));
    }
  }

  async createWholeToAdmin(order: any) {
    const calc = this.getTotalTaxAmounts(order.products);
    calc.target = '';

    const generatedAt = new Date()
      .toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
      .replace(',', '');

    order = Object.assign(order, { calc, generatedAt });
    order = await this.withPdfImages(order);

    this.alert.presentToast('Bitte warten... (ca. 15 Sekunden)', 9000);

    const res = await this.dataserver.generateWhole(order);
    if (res.status === 200 || res.status === 201) {
      this.alert.presentToast('Bestätigung wurde erfolgreich erstellt und versendet');
    } else {
      this.alert.presentConfirmationAlert('Fehler', JSON.stringify(res.data));
    }
  }

  getTotalAmounts(products: any[]): { totalQuantity: number; totalPrice: number } {
    const totalQuantity = products.reduce(
      (sum, product) => sum + this.getTotalQuantity(product.sizes),
      0
    );
    const totalPrice = products.reduce(
      (sum, product) => sum + this.getTotalPrice(product),
      0
    );
    return { totalQuantity, totalPrice };
  }

  getTotalQuantity(sizes: any[]): number {
    return sizes.reduce((total, size) => total + size.quantity, 0);
  }

  getTotalPrice(product: any): number {
    const totalQuantity = this.getTotalQuantity(product.sizes);
    return totalQuantity * parseFloat(product.price);
  }

  getSizeText(product: any): string {
    return product.sizes
      .map((size: any) => `${size.size}: ${size.quantity}`)
      .join('; ');
  }

  getTotalTaxAmounts(products: any[]): {
    netPrice: number;
    taxAmount: number;
    totalPrice: number;
    totalCollections: number;
    target: string;
    adminEmail: string;
  } {
    const { totalQuantity, totalPrice } = this.getTotalAmounts(products);
    const taxAmount = totalPrice * 0.19;
    const grossPrice = totalPrice + taxAmount;

    return {
      netPrice: totalPrice,
      taxAmount,
      totalPrice: grossPrice,
      totalCollections: totalQuantity,
      target: '',
      adminEmail: '',
    };
  }
}
